/**
 * Demo page showcasing selections viewer for IBS 2023
 * @module
 *
 */
import { createFileRoute } from "@tanstack/react-router";
import SelectionsViewer from "lib/components/selections/SelectionsViewer";
import styles from "lib/components/selections/SelectionsViewer.module.scss";
import useSelectionsViewer from "lib/components/selections/useSelectionsViewer";
import type { SelectionsViewerContextProps } from "lib/state/SelectionsViewerContext";
import SelectionsViewerContext from "lib/state/SelectionsViewerContext";
import { getSceneVersionManifestJson } from "lib/storage/selectionsStorage";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { parseCompositorManifest } from "selections-compositor/state/selections";
import useAppContext from "ui/hooks/useAppContext";

const SCENE_VERSION_ID = "20230119-epcon-bathroom";

function DemoPhotorealSelectionsPanoramaViewer() {
  const { setUserNotification } = useAppContext();

  const {
    manifest,
    setManifest,
    activeSelections,
    scene,
    setScene,
    menuItems,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
  } = useSelectionsViewer({
    onActiveSelections: async () => {
      setTilesRefreshLastTimestamp(Date.now());
    },
  });

  useEffect(() => {
    document.body.style.overflow = "hidden"; // Important to prevent scrolling on iPad
  }, []);

  const [tilesRefreshLastTimestamp, setTilesRefreshLastTimestamp] = useState<number>();

  useEffect(() => {
    async function fetchAndSetScene() {
      try {
        const manifestJson = await getSceneVersionManifestJson(SCENE_VERSION_ID);
        if (manifestJson) {
          const newManifest = parseCompositorManifest(manifestJson);
          setManifest(newManifest);
        }
      } catch {
        setUserNotification({
          message: "Cannot load scene",
          messageType: "error",
        });
      }
    }
    void fetchAndSetScene();
  }, []);

  const selectionsViewerContext: SelectionsViewerContextProps = {
    manifest,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
    menuItems,
    activeSelections,
  };

  return (
    <>
      <Head>
        <title>{getPageTitle("Demo - Photoreal Selections")}</title>
      </Head>

      <SelectionsViewerContext.Provider value={selectionsViewerContext}>
        <div className={styles.selectionsPage}>
          {manifest && (
            <SelectionsViewer tilesRefreshLastTimestamp={tilesRefreshLastTimestamp} scene={scene} setScene={setScene} />
          )}
        </div>
      </SelectionsViewerContext.Provider>
    </>
  );
}

// This page is, by design, public. Used for demos, it should not require any authentication.
//
// Th only obfuscation is through the page name.
export const Route = createFileRoute("/demo/photoreal-selections360-WX34VCTU45P2")({
  component: DemoPhotorealSelectionsPanoramaViewer,
  staticData: { isPublicPage: true },
});
